/**
 * Bundle Section JavaScript
 * Handles carousel, thumbnails, and frequency selector functionality
 *
 * Element targeting uses data attributes for JS hooks (decoupled from CSS classes)
 * State changes use both data attributes and classes (for CSS styling)
 */

class BundleSection {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;

    // Use data attributes for element targeting (not classes)
    this.mainCarousel = section.querySelector('[data-bundle-carousel="main"]');
    this.thumbsCarousel = section.querySelector('[data-bundle-carousel="thumbs"]');
    this.dotsContainer = section.querySelector('[data-bundle-dots]');
    this.prevButton = section.querySelector('[data-bundle-nav="prev"]');
    this.nextButton = section.querySelector('[data-bundle-nav="next"]');
    this.frequencyOptions = section.querySelectorAll('[data-bundle-option]');

    this.mainSwiper = null;
    this.thumbsSwiper = null;

    this.init();
  }

  init() {
    this.initCarousel();
    this.initFrequencySelector();
    this.initAddToCart();
  }

  initCarousel() {
    if (!this.mainCarousel) return;
    if (typeof Swiper === 'undefined') {
      console.warn('Swiper not loaded for bundle section');
      return;
    }

    // Initialize thumbnails swiper first (if thumbnails exist)
    if (this.thumbsCarousel) {
      this.thumbsSwiper = new Swiper(this.thumbsCarousel, {
        slidesPerView: 5,
        spaceBetween: 8,
        watchSlidesProgress: true,
        speed: 300
      });
    }

    // Build swiper config
    const swiperConfig = {
      slidesPerView: 1,
      spaceBetween: 0,
      speed: 350,
      grabCursor: true,
      navigation: {
        nextEl: this.nextButton,
        prevEl: this.prevButton
      }
    };

    // Add pagination if dots container exists
    if (this.dotsContainer) {
      swiperConfig.pagination = {
        el: this.dotsContainer,
        clickable: true,
        bulletClass: 'bundle-hero__carousel-dot',
        bulletActiveClass: 'bundle-hero__carousel-dot--active'
      };
    }

    // Add thumbs if available
    if (this.thumbsSwiper) {
      swiperConfig.thumbs = {
        swiper: this.thumbsSwiper
      };
    }

    // Initialize main swiper
    this.mainSwiper = new Swiper(this.mainCarousel, swiperConfig);

    // Thumbnail click handler with smart jump logic
    if (this.thumbsCarousel && this.thumbsSwiper) {
      const thumbSlides = this.thumbsCarousel.querySelectorAll('.swiper-slide');
      thumbSlides.forEach((slide, index) => {
        slide.addEventListener('click', (e) => {
          e.preventDefault();

          const slidesPerView = 5;
          const currentSlideIndex = this.thumbsSwiper.activeIndex;
          const visibleEnd = currentSlideIndex + slidesPerView - 1;
          const totalSlides = this.thumbsSwiper.slides.length;

          // Jump logic for edge slides
          if (index < currentSlideIndex) {
            this.thumbsSwiper.slideTo(index);
          } else if (index > visibleEnd) {
            this.thumbsSwiper.slideTo(Math.max(0, index - slidesPerView + 1));
          } else if (index === visibleEnd && index < totalSlides - 1) {
            this.thumbsSwiper.slideTo(currentSlideIndex + 1);
          } else if (index === currentSlideIndex && index > 0) {
            this.thumbsSwiper.slideTo(Math.max(0, currentSlideIndex - 1));
          }

          this.mainSwiper.slideTo(index);
        });
      });
    }
  }

  initFrequencySelector() {
    if (!this.frequencyOptions.length) return;

    // Get the product form for updating selling plan
    this.productForm = this.section.querySelector('[data-product-form]');
    this.sellingPlanInput = this.productForm?.querySelector('[data-selling-plan-id]');

    // Get pricing element for updates
    this.pricingBlock = this.section.querySelector('[data-bundle-pricing]');

    // Seed the purchase state from the option rendered active, so CSS keyed on it
    // (e.g. the Buy Once gift strikethrough) is right before the first click.
    const activeOption = this.section.querySelector('[data-bundle-option][data-active="true"]');
    if (activeOption) this.section.dataset.purchaseOption = activeOption.dataset.bundleOption;

    this.frequencyOptions.forEach(option => {
      option.addEventListener('click', () => {
        const optionType = option.dataset.bundleOption;

        // Update active state using data attribute
        this.frequencyOptions.forEach(opt => {
          opt.dataset.active = 'false';
          opt.classList.remove('bundle-hero__option--active');
        });
        option.dataset.active = 'true';
        option.classList.add('bundle-hero__option--active');

        const input = option.querySelector('input');
        if (input) input.checked = true;

        // Update the selling plan in the cart form
        this.updateSellingPlan(option);

        // Update the pricing display
        this.updatePricingDisplay(optionType);

        // Toggle onetime state on section for CSS targeting
        this.section.dataset.purchaseOption = optionType;

        // Dispatch custom event for other components to listen
        this.section.dispatchEvent(new CustomEvent('bundle:frequency-change', {
          detail: {
            value: input?.value,
            optionType: optionType,
            sellingPlanId: option.dataset.sellingPlanId || '',
            sectionId: this.sectionId
          },
          bubbles: true
        }));
      });
    });
  }

  updateSellingPlan(selectedOption) {
    if (!this.sellingPlanInput) return;

    const optionType = selectedOption.dataset.bundleOption;
    if (optionType === 'subscription') {
      // Get selling plan ID from the selected subscription option
      const sellingPlanId = selectedOption.dataset.sellingPlanId || '';
      this.sellingPlanInput.value = sellingPlanId;
    } else {
      // One-time purchase - no selling plan
      this.sellingPlanInput.value = '';
    }
  }

  updatePricingDisplay(optionType) {
    if (!this.pricingBlock) return;

    const priceEl = this.pricingBlock.querySelector('[data-bundle-price]');
    const comparePriceEl = this.pricingBlock.querySelector('[data-bundle-compare-price]');
    const discountTagEl = this.pricingBlock.querySelector('[data-bundle-discount-tag]');
    const perServingEl = this.pricingBlock.querySelector('[data-bundle-per-serving]');

    if (optionType === 'subscription') {
      // Show subscription pricing
      const subPrice = this.pricingBlock.dataset.subscriptionPrice;
      const subComparePrice = this.pricingBlock.dataset.subscriptionComparePrice;
      const subDiscountTag = this.pricingBlock.dataset.subscriptionDiscountTag;
      const subPerServing = this.pricingBlock.dataset.subscriptionPerServing;

      if (priceEl && subPrice) {
        priceEl.textContent = subPrice;
      }

      if (comparePriceEl) {
        if (subComparePrice && subComparePrice !== subPrice) {
          comparePriceEl.textContent = subComparePrice;
          comparePriceEl.hidden = false;
        } else {
          comparePriceEl.hidden = true;
        }
      }

      if (discountTagEl) {
        if (subDiscountTag) {
          discountTagEl.textContent = subDiscountTag;
          discountTagEl.hidden = false;
        } else {
          discountTagEl.hidden = true;
        }
      }

      if (perServingEl && subPerServing) {
        perServingEl.textContent = subPerServing;
        perServingEl.hidden = false;
      }
    } else {
      // Show one-time pricing
      const oneTimePrice = this.pricingBlock.dataset.onetimePrice;
      const oneTimeComparePrice = this.pricingBlock.dataset.onetimeComparePrice;
      const oneTimePerServing = this.pricingBlock.dataset.onetimePerServing;

      if (priceEl && oneTimePrice) {
        priceEl.textContent = oneTimePrice;
      }

      if (comparePriceEl) {
        if (oneTimeComparePrice) {
          comparePriceEl.textContent = oneTimeComparePrice;
          comparePriceEl.hidden = false;
        } else {
          comparePriceEl.hidden = true;
        }
      }

      // Hide discount tag for one-time purchases (typically no discount)
      if (discountTagEl) {
        discountTagEl.hidden = true;
      }

      if (perServingEl) {
        if (oneTimePerServing) {
          perServingEl.textContent = oneTimePerServing;
          perServingEl.hidden = false;
        } else {
          perServingEl.hidden = true;
        }
      }
    }
  }

  initAddToCart() {
    if (!this.productForm) return;

    this.submitButton = this.productForm.querySelector('[data-bundle-submit]');
    if (!this.submitButton) return;

    this.productForm.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleAddToCart();
    });
  }

  async handleAddToCart() {
    if (this.isLoading) return;

    this.isLoading = true;
    this.submitButton.dataset.loading = 'true';
    this.submitButton.classList.add('c-button--loading');

    try {
      const variantInput = this.productForm.querySelector('input[name="id"]');
      const sellingPlanInput = this.productForm.querySelector('input[name="selling_plan"]');
      const productIdInput = this.productForm.querySelector('input[data-product-id]');

      if (!variantInput) {
        throw new Error('No variant selected');
      }

      const variantId = variantInput.value;
      let itemsToAdd = [{
        id: variantId,
        quantity: 1
      }];

      // Add selling plan if selected
      if (sellingPlanInput && sellingPlanInput.value) {
        itemsToAdd[0].selling_plan = sellingPlanInput.value;
      }

      // Buy Once with "Remove free gifts on Buy Once" on: tag the line so the cart's gift
      // reconciliation (validateCartGifts) doesn't re-add the gifts withheld below.
      const otpNoGifts = this.isOtpNoGifts();
      if (otpNoGifts) {
        itemsToAdd[0].properties = {
          _otp_no_gifts: 'true',
          _otp_kept_gifts: this.section.dataset.otpKeptGiftHandles || ''
        };
      }

      // Resolve free gifts: dynamic sale first, legacy fallback (see getGiftProductsToAdd in theme.liquid)
      if (productIdInput) {
        const productId = productIdInput.getAttribute('data-product-id');
        let giftProducts = await window.getGiftProductsToAdd(productId);
        if (otpNoGifts) {
          giftProducts = giftProducts.filter(gift => this.isGiftKeptOnOtp(gift.id));
        }
        if (giftProducts.length > 0) {
          itemsToAdd = itemsToAdd.concat(giftProducts);
        }
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items: itemsToAdd })
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.description || result.message || 'Add to cart failed');
      }

      // Dispatch event for other components
      document.dispatchEvent(new CustomEvent('cart:item-added', {
        detail: { items: itemsToAdd },
        bubbles: true
      }));

      // Redirect to cart page with locale support (same as shop-all)
      this.redirectToCart();

    } catch (error) {
      console.error('Add to cart error:', error);
      this.showError(error.message);
      this.isLoading = false;
      this.submitButton.dataset.loading = 'false';
      this.submitButton.classList.remove('c-button--loading');
    }
  }

  isOtpNoGifts() {
    return this.section.dataset.otpNoGifts === 'true' && this.section.dataset.purchaseOption === 'onetime';
  }

  // Same rule as createGiftGuard in ajax-cart.js, with dynamic-sale gift handles
  // included alongside the legacy freeGiftHandles map.
  isGiftKeptOnOtp(variantId) {
    const keptHandles = (this.section.dataset.otpKeptGiftHandles || '')
      .split(',')
      .map(handle => handle.trim().toLowerCase())
      .filter(Boolean);
    if (!keptHandles.length) return false;

    const id = String(variantId);
    let handle = (window.freeGiftHandles || {})[id];
    if (!handle) {
      const saleGifts = (window.rz && window.rz.sale && window.rz.sale.freeGifts) || [];
      const saleGift = saleGifts.find(gift =>
        String(gift.variantId).replace('gid://shopify/ProductVariant/', '') === id
      );
      handle = saleGift && saleGift.handle;
    }
    return !!handle && keptHandles.includes(String(handle).toLowerCase());
  }

  redirectToCart() {
    const path = window.location.pathname;
    if (path.includes('/es')) {
      window.location.href = '/es/cart';
    } else if (path.includes('/fr')) {
      window.location.href = '/fr/cart';
    } else {
      window.location.href = '/cart';
    }
  }

  showError(message) {
    let errorEl = this.section.querySelector('[data-bundle-error]');
    if (!errorEl) {
      errorEl = document.createElement('div');
      errorEl.className = 'bundle-hero__error';
      errorEl.setAttribute('role', 'alert');
      errorEl.setAttribute('data-bundle-error', '');
      this.productForm.parentNode.insertBefore(errorEl, this.productForm.nextSibling);
    }

    errorEl.textContent = message || 'Unable to add item to cart. Please try again.';
    errorEl.hidden = false;

    setTimeout(() => {
      errorEl.hidden = true;
    }, 5000);
  }

  destroy() {
    if (this.mainSwiper) {
      this.mainSwiper.destroy(true, true);
    }
    if (this.thumbsSwiper) {
      this.thumbsSwiper.destroy(true, true);
    }
  }
}

// Custom element for Shopify section rendering
class BundleInformationSection extends HTMLElement {
  constructor() {
    super();
    this.bundleSection = null;
  }

  connectedCallback() {
    this.bundleSection = new BundleSection(this);
  }

  disconnectedCallback() {
    if (this.bundleSection) {
      this.bundleSection.destroy();
    }
  }
}

// Register custom element
if (!customElements.get('bundle-information')) {
  customElements.define('bundle-information', BundleInformationSection);
}

// Initialize on DOMContentLoaded for non-custom-element usage
document.addEventListener('DOMContentLoaded', function() {
  const sections = document.querySelectorAll('[data-section-type="bundle-information"]:not(bundle-information)');
  sections.forEach(section => {
    new BundleSection(section);
  });
});

// Shopify Section API for theme editor
if (Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function(event) {
    const section = event.target.querySelector('[data-section-type="bundle-information"]');
    if (section && !section.bundleSection) {
      section.bundleSection = new BundleSection(section);
    }
  });

  document.addEventListener('shopify:section:unload', function(event) {
    const section = event.target.querySelector('[data-section-type="bundle-information"]');
    if (section && section.bundleSection) {
      section.bundleSection.destroy();
    }
  });
}
